import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Bell, Moon, Sun, Menu, X, Loader2, BookOpen, Code2, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/context/AuthContext'
import { useTheme } from '@/context/ThemeContext'
import { PlanBadge } from '@/components/shared/PlanBadge'
import { api } from '@/lib/api'
import { DOCS_URL, API_DOCS_URL } from '@/lib/constants'
import { toast } from 'sonner'

interface TopBarProps {
  onMenuClick: () => void
  sidebarCollapsed: boolean
}

interface Notification {
  id: string
  title: string
  message: string
  severity: 'info' | 'warning' | 'critical' | 'maintenance'
  created_at: string
  ends_at: string | null
}

const severityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-500/10 text-red-500 border-red-500/20'
    case 'warning':
      return 'bg-amber-500/10 text-amber-500 border-amber-500/20'
    case 'maintenance':
      return 'bg-blue-500/10 text-blue-500 border-blue-500/20'
    default:
      return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
  }
}

export function TopBar({ onMenuClick, sidebarCollapsed }: TopBarProps) {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/account/notifications')
      setNotifications(res.data.notifications || [])
    } catch {
      // fail silently; the bell just shows empty
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotifications()
  }, [])

  const handleDismiss = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    try {
      await api.post(`/account/notifications/${id}/dismiss`)
      setNotifications((prev) => prev.filter((n) => n.id !== id))
    } catch {
      toast.error('Failed to dismiss notification')
    }
  }

  const handleClick = async (notification: Notification) => {
    try {
      await api.post(`/account/notifications/${notification.id}/click`)
    } catch {
      // ignore tracking errors
    }
    toast.info(`${notification.title}: ${notification.message}`)
    setOpen(false)
  }

  const unreadCount = notifications.length

  // CHECKPOINT: PRD-10 FR-10.1.4 — Top bar spans full width on mobile, matches sidebar on desktop
  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-30 flex h-[68px] items-center justify-between border-b border-border/80 bg-card/75 px-4 backdrop-blur-xl transition-[left] duration-200 sm:px-6',
        'left-0',
        sidebarCollapsed ? 'md:left-[68px]' : 'md:left-[240px]'
      )}
    >
      <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={onMenuClick} aria-label="Open menu">
        <Menu className="h-4 w-4" />
      </Button>

      <div className="hidden text-sm text-muted-foreground md:block">
        Account workspace
      </div>

      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative h-9 w-9" aria-label="Notifications">
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 max-h-[28rem] overflow-y-auto">
            <div className="px-3 py-2 border-b">
              <p className="text-sm font-medium">Notifications</p>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                No active notifications
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-3 cursor-pointer hover:bg-accent/50 transition-colors ${severityColor(notification.severity)} border-l-4`}
                    onClick={() => handleClick(notification)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{notification.title}</p>
                        <p className="text-xs mt-0.5 line-clamp-3">{notification.message}</p>
                        <p className="text-[10px] mt-1 opacity-70">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 -mr-1 -mt-1"
                        onClick={(e) => handleDismiss(e, notification.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-9 gap-2 rounded-lg px-2" aria-label="Account menu">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                <span className="text-[10px] font-bold text-white">
                  {user?.display_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                </span>
              </div>
              <span className="text-sm hidden sm:inline">{user?.display_name || user?.email}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.display_name || 'User'}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
              <div className="mt-1.5">
                <PlanBadge plan={user?.plan || 'starter'} />
              </div>
            </div>
            <DropdownMenuSeparator />
            {/* CHECKPOINT: PRD-10 §4.3 — User dropdown docs and API reference links. */}
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer">Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a
                href={DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer flex items-center justify-between"
              >
                <span className="flex items-center gap-2"><BookOpen className="h-4 w-4" /> Docs</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a
                href={API_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer flex items-center justify-between"
              >
                <span className="flex items-center gap-2"><Code2 className="h-4 w-4" /> API Reference</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={logout} className="text-destructive cursor-pointer">
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
